import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
import { StateContext } from '../../contexts/StateContext';
import {
    Tabs,
    Tab,
    Box,
    Accordion,
    AccordionDetails,
    Typography,
    AccordionSummary,
    Card,
    CardContent,
    Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import parse from 'html-react-parser';
import { useLocation } from 'react-router-dom';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import FileDownloadOffIcon from '@mui/icons-material/FileDownloadOff';
import ArticleIcon from '@mui/icons-material/Article';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LockIcon from '@mui/icons-material/Lock';
import { ScoreCard } from '@brightlayer-ui/react-components';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import VideoComponent from './VideoComponent';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { fetchProducts, openProductsMenu } from './ProductHomeComponent';
import { useTranslation } from 'react-i18next';
import { sortProductVersions } from '../../utils/versionSorter';

function TabPanel(props) {
    const { children, value, index } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
            {value === index && <Box p={3}> {children}</Box>}
        </div>
    );
}

export const alterParentName = (parentKey) => {
    if (parentKey === 'versions') {
        return 'Previous Versions';
    } else if (parentKey === 'marketingsDocuments') {
        return 'Marketing Documents';
    } else if (parentKey === 'drawings') {
        return 'Drawings';
    } else if (parentKey === 'productLifeCycles') {
        return 'Life Cycles';
    } else if (parentKey === 'technicalBulletins') {
        return 'Technical Bulletins';
    } else if (parentKey === 'productChangeNotice') {
        return 'Product Change Notices';
    }
    else
    {
        return parentKey;
    }
};

export const genereateHrefForDocs = (product, item) => {
    return `/api/v1/download/${product?.id}?file=${encodeURIComponent(item?.path)}`;
};

export const genereateHrefForNestedDocs = (product, item, version, updateId) => {
    return `/api/v1/download/${product?.id}/${version?.id}/${updateId}?file=${encodeURIComponent(item?.path)}`;
};

export const generateReleseNoteDocUrl = (product, version) => {
    return `/api/v1/download/${product?.id}/${version?.name}/releasenotes?language=en`;
};

export const generateSecurityBulletinUrl = (product, version, update, file) => {
    return `/api/v1/download/${product?.id}/${version?.name}/${update?.version}?file=${encodeURIComponent(file.path)}`;
};

export const generateDownloadVersionUrl = (product, version, update, installerPath) => {
    return `/api/v1/download/${product?.id}/${version?.name}/${update?.version}?file=${installerPath}`;
};

export const showNestedDocuments = (version, product, calledFor) => {
    if (!version?.updates?.length) return null;
    const renderTree = (node, key) => {
        if (node?.files?.length) {
            return (
                <TreeItem
                    nodeId={key}
                    label={node?.name}
                    className="grid-tree-item"
                    data-testid="tree-item"
                >
                    {node?.files?.map((child, index) => renderTree(child, `${key}-${index}`))}
                </TreeItem>
            );
        }

        return (
            <li className="nested-tree-items li-ml-2" data-testid="nested-tree-item">
                <Link
                    className="document-link show-documents-link"
                    href={genereateHrefForNestedDocs(product, node, version, version?.updates[0]?.id)}
                    target="_blank"
                    rel="noopener"
                    data-testid="link"
                >
                    {node?.name}
                </Link>
                <br />
            </li>
        );
    };

    const collectNodeIds = (node, key) => {
        if (!node?.files?.length) return [key];
        return [key, ...node.files.flatMap((child, index) => collectNodeIds(child, `${key}-${index}`))];
    };

    const defaultExpanded = version?.updates[0]?.documentations?.flatMap((doc, key) =>
        collectNodeIds(doc, `${key}`)
    );

    return version?.updates[0]?.documentations?.map((everyDocument, key) => (
        <TreeView
            key={key}
            aria-label="file system navigator"
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            className={`grid-tree-view ${calledFor}`}
            defaultExpanded={defaultExpanded}
            data-testid="tree-view-root"
        >
            {renderTree(everyDocument, `${key}`)}
        </TreeView>
    ));
};

export const extractAnchor = (input, productId, versionId) => {
    const anchorTags = input?.match(/a[^>]*>.*?<\/a>/g);
    if (!anchorTags) return '';
    let desiredString = anchorTags;
    desiredString[0] = desiredString[0]?.split(' ')[2]?.replace('{productId}', productId);
    desiredString[0] = desiredString[0]?.replace('{versionId}', versionId);
    return desiredString.toString() + '"';
    }

export const isEmeddedAnchor = (input) => {
    const anchorTags = input?.match(/a[^>]*>.*?<\/a>/g);
    if (anchorTags) {
        return true;
    } else {
        return false;
    }
    }

const ViewProductComponent = (props) => {
    const [product, setProduct] = useState({});
    const location = useLocation();
    const { pageHeader, setPageHeader } = useContext(StateContext);
    const { reqProducts } = useContext(StateContext);
    const { showVideo, toggleVideoShow } = useContext(StateContext);
    const {setProdProductLink} = useContext(StateContext);
    const { setAllProducts} = useContext(StateContext);
    const [pathName, setPathName] = useState('');
    const { t } = useTranslation();
    const [clickedVideo, setClickedVideo] = useState(null);

    useEffect(() => {
       if(location?.pathname != pathName){
            setPathName(location?.pathname);
        }
    },[location?.pathname]);

    useEffect(() => {
        if (!localStorage.getItem('reloaded')) {
            let productItem = reqProducts;
            const prodId = window?.location?.pathname?.split('/')?.reverse()[0];
            productItem = productItem?.filter((item) => item.id === prodId);
            productItem = productItem && productItem?.length > 0 && productItem[0];

            // Sort versions in descending order before setting the product
            const productWithSortedVersions = sortProductVersions(productItem);
            setProduct({ ...productWithSortedVersions });
            window.sessionStorage.setItem('current-viewed-product', JSON.stringify(productWithSortedVersions));
        }
    },[pathName]);

    useEffect(() => {
        product && setPageHeader(product?.name);
    }, [product]);

    useEffect(() => {
        const pageHeaderKey = pageHeader?.toLowerCase().replace(/[&\/\\#, +()$~%.'":*?<>{}]/g, '-tql-')
        let currentMenuElement = document?.querySelector(`.${pageHeaderKey}`);
        currentMenuElement?.classList?.add('selected-nav-item');

        return () => {
            !showVideo && currentMenuElement?.classList?.remove('selected-nav-item')
        }
    })

    useEffect(() => {
        window.onbeforeunload = () => {
            localStorage.setItem('reloaded', true);
        };

        if (localStorage.getItem('reloaded')) {
            //Page reloaded
            fetchProducts(setAllProducts,setProdProductLink);
            const currentViewedProduct = JSON.parse(window.sessionStorage.getItem('current-viewed-product'));

            // Sort versions in descending order when retrieving from session storage
            const productWithSortedVersions = sortProductVersions(currentViewedProduct);
            setProduct({ ...productWithSortedVersions });
            openProductsMenu();
            localStorage.removeItem('reloaded');
        }

        return () => {
            window.onbeforeunload = null;
        };
    }, []);

    let documentNames = Array.from(
        new Set(
            product?.versions?.flatMap((version) =>
                version?.updates?.flatMap((update) =>
                    update?.documentations?.map((documentation) => documentation?.name)
                )
            )
        )
    );

    let documents = {};
    for (let name of documentNames) {
        documents[name] = product?.versions?.flatMap((version) =>
            version?.updates?.flatMap((update) =>
                update?.documentations
                    ?.filter((documentation) => documentation?.name === name)
                    ?.flatMap((documentation) => documentation?.files)
                    ?.map((file) => file?.name)
            )
        );
    }
    for (let key in documents) {
        documents[key] = [...new Set(documents[key])];
    }

    const [tabIndex, setTabIndex] = useState(0);
    const handleChange = (event, newTabIndex) => {
        setTabIndex(newTabIndex);
    };

    const renderAccordian = (data, parentKey, product) => {
        return (
            <Accordion key={parentKey} defaultExpanded={true} className='grid-accordian'>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" className='accordian-sum'>
                    <Typography className="document-heading show-documents-summary">
                        {alterParentName(parentKey)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails className='accordian-det'>
                    <ul className={alterParentName(parentKey) == 'Life Cycles' ? 'remove-ul-left-margin' : ''}>
                        {data?.map((item, index) => {
                            return (
                                <li key={index}>
                                    {Array.isArray(item) ? (
                                        renderAccordian(item, `${parentKey}-nested-${index}`, product)
                                    ) : (
                                        <li>
                                            <Link
                                                className="show-documents-link"
                                                target="_blank"
                                                rel="noopener"
                                                href={genereateHrefForDocs(product, item)}
                                            >
                                                {' '}
                                                {item?.name}
                                            </Link>
                                        </li>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </AccordionDetails>
            </Accordion>
        );
    };
    const showDocumentAccordian = (product) => {
        const keysToExclude = ['videos', 'versions', 'poemails'];
        return (
            <div className="view-product-documents">
                {Object.keys(product).map((key) => {
                    if (Array.isArray(product[key]) && !keysToExclude.includes(key)) {
                        return renderAccordian(product[key], key, product);
                    }
                    return null;
                })}
            </div>
        );
    };


    const videoClickHandler = (product) => {
        setClickedVideo(product);
        toggleVideoShow(true);
    };

    const handleDownload = (e, product, eachVersion, eachUpdate, path) => {
        e.preventDefault();
        const downloadUrl = generateDownloadVersionUrl(product, eachVersion, eachUpdate, path);
        window.open(downloadUrl, '_blank');
    };

    return (
        <>
            <div data-testid="path-name"  style={{ visibility: 'hidden' }} >{pathName}</div>
            {product && Object.keys(product).length > 0 && (
                <div className="viewProduct" data-testid="view-product">
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabIndex} onChange={handleChange}>
                            <Tab label= {t('translation:pages.products.view_product_component.download')} id="tab-download"></Tab>
                            <Tab label={t('translation:pages.products.view_product_component.documentation')} id="tab-documentation"></Tab>
                            <Tab label={t('translation:pages.products.view_product_component.videos')} id="tab-videos"></Tab>
                        </Tabs>
                        <Card variant="outlined">
                            <CardContent>
                                {!product?.name?.includes('DTPG') ?
                                    <TabPanel value={tabIndex} index={0}>
                                        <div className="download-segment">
                                            <p className="p-description">{product?.descriptionEN}</p>
                                            <br />

                                            <span className="view-product-component">
                                                <blockquote className="blockquote">
                                                    <div className="warning-container">
                                                        <WarningIcon />
                                                        <p>
                                                        {t('translation:pages.products.view_product_component.blockQuoteText')}                                                            }
                                                        </p>
                                                    </div>
                                                </blockquote>
                                            </span>

                                            {product?.versions?.map((eachVersion) => {
                                                const productId = product.id;
                                                const versionId = eachVersion.id;
                                                let showedNotice;
                                                if (isEmeddedAnchor(eachVersion.noticeEN)) {
                                                    showedNotice = parse(
                                                        eachVersion.noticeEN.replace(
                                                            /href="([^"]*)"/,
                                                            extractAnchor(eachVersion.noticeEN, productId, versionId)
                                                        )
                                                    );
                                                } else {
                                                    showedNotice = parse(eachVersion?.noticeEN || " " );
                                                }

                                                return (
                                                    <div className="each-version-rendering">
                                                        <div className="version-heading">
                                                            <div className="version-title">{eachVersion?.name}</div>
                                                            {eachVersion &&
                                                                eachVersion.releaseNotes &&
                                                                eachVersion.releaseNotes[0] && (
                                                                    <div className="version-release-notes">
                                                                        <Link
                                                                            className="link-version-release-notes"
                                                                            target="_blank"
                                                                            rel="noopener"
                                                                            href={generateReleseNoteDocUrl(
                                                                                product,
                                                                                eachVersion
                                                                            )}
                                                                        >
                                                                            {'(View Release Notes)'}
                                                                        </Link>
                                                                    </div>
                                                                )}
                                                        </div>

                                                        <div className="version-notice">
                                                            <p>{showedNotice}</p>
                                                        </div>

                                                        <div className="grid-cards">
                                                            {eachVersion?.updates?.map((eachUpdate) => {
                                                                return (
                                                                    <div className="display-version-wrapper">
                                                                        <ScoreCard
                                                                            sx={{ width: 350 }}
                                                                            headerTitle={eachUpdate?.displayVersion}
                                                                            headerSubtitle={eachUpdate?.generated}
                                                                        >
                                                                            <List className='score-card-list'>
                                                                                <ListItem className="li-security-bulletins">
                                                                                    <ListItemText
                                                                                        primary={
                                                                                            eachUpdate?.securityBulletins ? (
                                                                                                <h3>{t('translation:pages.products.view_product_component.securityBulletin')}</h3>
                                                                                            ) : (
                                                                                                <h3>
                                                                                                  {t('translation:pages.products.view_product_component.noSecurityBulletin')}
                                                                                                  
                                                                                                </h3>
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </ListItem>
                                                                            </List>

                                                                            {eachUpdate?.securityBulletins && (
                                                                                <List className={`${eachUpdate.securityBulletins.length >  1 ? 'security-bulletins-wrapper-ul-multiple' : 'security-bulletins-wrapper-ul'}`}> 
                                                                                    <ListItem className="security-bulletins-wrapper">
                                                                                        {eachUpdate?.securityBulletins?.map(
                                                                                            (value) => {
                                                                                                return (
                                                                                                    <div className="security-bulletins">
                                                                                                        <SecurityIcon />
                                                                                                        <Link
                                                                                                            target="_blank"
                                                                                                            rel="noopener"
                                                                                                            href={generateSecurityBulletinUrl(
                                                                                                                product,
                                                                                                                eachVersion,
                                                                                                                eachUpdate,
                                                                                                                value
                                                                                                            )}
                                                                                                        >
                                                                                                            {value?.name}
                                                                                                        </Link>
                                                                                                    </div>
                                                                                                );
                                                                                            }
                                                                                        )}
                                                                                    </ListItem>
                                                                                </List>
                                                                            )}
                                                                            {eachUpdate.hasOwnProperty('installers') &&
                                                                                eachUpdate['installers']?.length > 0 && (
                                                                                    <List>
                                                                                        <ListItem className="li-installers">
                                                                                            {eachUpdate['installers']?.map(
                                                                                                (installer) => {
                                                                                                return [installer]?.map(
                                                                                                    (elem) => {
                                                                                                        return (
                                                                                                            <div className="installer-wrapper">
                                                                                                                <div className="download-segment-installer">
                                                                                                                    <CloudDownloadIcon />
                                                                                                                    <Link
                                                                                                                            to='#'
                                                                                                                            onClick={(e) => handleDownload(e, product, eachVersion, eachUpdate, elem['path'])}
                                                                                                                    >
                                                                                                                        {`Download ${eachUpdate?.displayVersion} version`}
                                                                                                                    </Link>
                                                                                                                </div>
                                                                                                                {elem.hasOwnProperty(
                                                                                                                    'sha256'
                                                                                                                ) && (
                                                                                                                    <div className="sha-key-segment">
                                                                                                                        <LockIcon />
                                                                                                                        <span>
                                                                                                                            SHA-256:
                                                                                                                            {
                                                                                                                                elem[
                                                                                                                                    'sha256'
                                                                                                                                ]
                                                                                                                            }
                                                                                                                        </span>
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        );
                                                                                                    }
                                                                                                );
                                                                                                }
                                                                                            )}
                                                                                        </ListItem>
                                                                                    </List>
                                                                                )}
                                                                        </ScoreCard>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </TabPanel> :
                                    (tabIndex === 0) ? (
                                    <>
                                        <div className="download-segment">
                                                <p className="p-description" data-testid="p-descr">{product?.descriptionEN}</p>
                                            <br />
                                        </div>
                                        <div className="no-video-available">
                                            <FileDownloadOffIcon />
                                            <p className="no-video-available-text">
                                                {t('translation:pages.products.view_product_component.noDownloadAvailable')}
                                            </p>
                                        </div>
                                    </>
                                    ) : <></>
                                }

                                {!product?.name?.includes('DTPG') ?
                                    <TabPanel value={tabIndex} index={1}>
                                        {showDocumentAccordian(product)}
                                        {product.hasOwnProperty('versions') && (
                                            <>
                                                <Accordion defaultExpanded={true} className='grid-accordian'>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        aria-controls="panel1a-content"
                                                    >
                                                        <Typography className="show-documents-summary document-heading">{`Current Version (${product?.versions[0]?.name.replace(
                                                            'Version',
                                                            ''
                                                        )?.trim()})`}</Typography>
                                                    </AccordionSummary>
                                                    {showNestedDocuments(product.versions[0], product,'current-version')}
                                                </Accordion>
                                                {product?.versions?.slice(1)?.length > 0 && (
                                                    <Accordion defaultExpanded={true} className='grid-accordian'>
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            aria-controls="panel1a-content"
                                                        >
                                                            <Typography className="show-documents-summary document-heading">{`Previous Versions`}</Typography>
                                                        </AccordionSummary>

                                                        {product?.versions?.slice(1)?.map((eachVersion,key) => {
                                                            return (
                                                                <>
                                                                    <TreeView
                                                                        aria-label="file system navigator"
                                                                        defaultCollapseIcon={<ExpandMoreIcon />}
                                                                        defaultExpandIcon={<ChevronRightIcon />}
                                                                        className="grid-tree-view"
                                                                        // defaultExpanded={nodeIds}
                                                                    >
                                                                        <TreeItem
                                                                            nodeId={key}
                                                                            label={eachVersion?.name?.replace('Version','')}
                                                                            className="grid-tree-item"
                                                                        >
                                                                            {showNestedDocuments(eachVersion, product,'previous-version')}
                                                                        </TreeItem>

                                                                    </TreeView>
                                                                </>
                                                            );
                                                        })}
                                                    </Accordion>
                                                )}
                                            </>
                                        )}
                                    </TabPanel> :
                                    (tabIndex === 1) ? (
                                    <div className="no-video-available">
                                        <ArticleIcon />
                                        <p className="no-video-available-text">
                                            {t('translation:pages.products.view_product_component.noDocumentationAvailable')}
                                        </p>
                                    </div>
                                    ) : <></>
                                }
                                <TabPanel value={tabIndex} index={2}>
                                    {!showVideo &&
                                        product?.videos?.map((el, key) => {
                                            return (
                                                <div>
                                                    <div className={`view-tab-videos`}>
                                                        <div
                                                            className="video-segment"
                                                            onClick={() => videoClickHandler(el)}
                                                        >
                                                            <img
                                                                // controls={controls}
                                                                src={`/api/v1/download/${product?.id}?file=${el?.poster}`}
                                                                className={`video video-${product?.id}`}
                                                                alt="Video thumbnail"
                                                            >
                                                                {/* <source
                                                            src={`/api/v1/download/${product?.id}?file=${el?.path}`}
                                                            type="video/mp4"
                                                        /> */}
                                                            </img>
                                                            <div className="text-segment-wrapper">
                                                                <div className="heading-segment">
                                                                    <Typography>
                                                                        {parse(el?.name).replace('.mp4', '')}
                                                                    </Typography>
                                                                </div>
                                                                <div className="description-segment">
                                                                    <Typography>{parse(el?.descriptionEN)}</Typography>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                    {showVideo && (
                                        <VideoComponent
                                            videoContent={clickedVideo}
                                            videoSrc={`/api/v1/download/${product?.id}?file=${clickedVideo?.path}`}
                                            productHeading={product.name}
                                        />
                                    )}

                                    {!product?.videos && (
                                        <div className="no-video-available">
                                            <VideoLibraryIcon />
                                            <p className="no-video-available-text">
                                            {t('translation:pages.products.view_product_component.noVideoAvailable')}
                                            </p>
                                        </div>
                                    )}
                                </TabPanel>
                            </CardContent>
                        </Card>
                    </Box>
                </div>
            )}
        </>
    );
};

export default ViewProductComponent;
